import { authService } from '../auth/auth.service';
import css from './login.module.less';
import { t, useTranslator } from '@/translations/translator';
import { submitClosestForm, submitOnEnter, useFieldFeedback } from '@/util/formFeedback';
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';
import CPromise from '@whiz-cart/node-shared/cPromise';
import { RequestError } from '@whiz-cart/node-shared/endpoint/index';
import { Button } from '@whiz-cart/ui-shared/button/button.component.tsx';
import { Input } from '@whiz-cart/ui-shared/form/input.component.tsx';
import inputCss from '@whiz-cart/ui-shared/form/input.module.less';
import { NavLink } from '@whiz-cart/ui-shared/routing/navLink.component.tsx';
import { urlService } from '@whiz-cart/ui-shared/url/url.service';
import { commonValidations } from '@whiz-cart/ui-shared/validations/commonValidations';
import { createForm, type Validations } from 'cross-state/react';
import _ from 'lodash';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

export interface LoginProps {
    fastLogin?: string;
    clearFastLogin?: () => void;
}

// Used for E2E tests, see: https://developers.cloudflare.com/turnstile/troubleshooting/testing/
const TURNSTILE_TEST_ENVIRONMENT_SITE_KEY = '1x00000000000000000000AA';

interface LoginDraft {
    userName: string;
    password: string;
    newPassword: string;
    newPasswordRepeat: string;
}

const { Form, Field, FormState } = createForm<LoginDraft>({
    defaultValue: { userName: '', password: '', newPassword: '', newPasswordRepeat: '' },
});

// User name and password are checked by handleLogin, so that a click on login explains what is missing
const changePasswordValidations: Validations<LoginDraft, LoginDraft> = function* ({ draft, fieldError }) {
    if (!commonValidations.password(draft.newPassword)) yield fieldError('newPassword', 'length');
    if (draft.newPasswordRepeat !== draft.newPassword) yield fieldError('newPasswordRepeat', 'repeat');
};

type ErrorField = keyof LoginDraft | 'captcha';

const errorCodes = {
    wrongPassword: 34005,
    invalidStoreToken: 34010,
    wrongStoreToken: 34011,
    noStoreToken: 34012,
    changePassword: 34004,
};

export default function Login({ fastLogin, clearFastLogin }: LoginProps) {
    const [changePassword, setChangePassword] = useState(false);
    const request = useRef<CPromise<unknown> | null>(null);

    const [isCaptchaEnabled = true] = authService.isCaptchaEnabled.useCache();

    const loginButton = useRef<HTMLDivElement>(null);
    const turnstileRef = useRef<TurnstileInstance>(null);
    const [turnstileToken, setTurnstileToken] = useState('');
    const isTurnstileProcessing = (config?.turnstileCaptcha.siteKey ?? '').length > 0 && isCaptchaEnabled && turnstileToken.length === 0;

    const testEnvironmentSecretToken = urlService.getUrlHashParams().testEnvironment;

    const [success, setSuccess] = useState<ReactNode>();
    const feedback = useFieldFeedback<ErrorField>();
    const { setError } = feedback;

    async function handleLogin({ userName, password, newPassword }: LoginDraft) {
        if (!userName) {
            setError('userName', t('profile.login.noUserName'));
            return;
        }
        if (!password) {
            setError('password', t('profile.login.noPassword'));
            return;
        }

        // Reset error overrides, otherwise errors will still be visible
        setError('userName', undefined);
        setError('password', undefined);
        setError('captcha', undefined);

        try {
            request.current?.cancel();

            if (changePassword) {
                const error = await (request.current = new CPromise((resolve, reject) =>
                    authService.changePassword(userName, password, newPassword).then(resolve).catch(reject),
                ));

                if (error) {
                    if ((error as any).data?.status === 34005) {
                        setError('password', 'Wrong password');
                    } else setError('password', 'Unknnown Error');
                    return;
                }
            }

            const error = await (request.current = new CPromise((resolve, reject) =>
                authService
                    .login(userName, newPassword || password, turnstileToken, testEnvironmentSecretToken)
                    .then(resolve)
                    .catch(reject),
            ));

            if (error) {
                throw error;
            }

            clearFastLogin?.();
        } catch (error) {
            console.error('Failed to login', error);
            // Captcha is only valid once -> reset, so user can try again ASAP
            turnstileRef.current?.reset();

            switch ((error as any).response?.status) {
                case 401: {
                    if (error instanceof RequestError) {
                        const result = z
                            .object({
                                isCaptchaSuccessful: z.boolean(),
                            })
                            .safeParse(error.data);

                        if (result.success && !result.data.isCaptchaSuccessful) {
                            setError('captcha', t('profile.login.captchaInvalidOrExpired'));
                            return;
                        }
                    }

                    setError('password', t('profile.login.wrongPasswordOrUser'));
                    break;
                }

                default:
                    if (error) {
                        const knownError = _.findKey(errorCodes, (x) => x === (error as any).data?.status) as
                            | keyof typeof errorCodes
                            | undefined;
                        if (knownError === 'changePassword') {
                            setSuccess('You have to change your password.');
                            setChangePassword(true);
                        } else if (knownError) setError('password', t(`profile.login.${knownError}`));
                        else setError('password', t('profile.login.unknownError'));
                    } else setError('password', 'Unknown Error');
            }
        }
    }

    const trans = useTranslator();
    const validationMessages = {
        userName: () => trans('profile.login.requiredField'),
        password: () => trans('profile.login.requiredField'),
        newPassword: (x: string) => trans('profile.login.minimumLength', { currentLength: x.length }),
        newPasswordRepeat: () => trans('profile.login.repeatNewPassword'),
    };

    const initialValues = useMemo((): LoginDraft => {
        const [, userName = '', password = ''] = fastLogin?.match(/([^:]+):(.+)/) ?? [];
        return { userName, password, newPassword: '', newPasswordRepeat: '' };
    }, [fastLogin]);

    function input(name: keyof LoginDraft, autoComplete: string) {
        return (
            <Field
                name={name}
                render={({ value, onChange }, { errors, hasTriggeredValidations }) => (
                    <Input
                        value={value}
                        onChange={(value) => {
                            onChange(value);
                            feedback.changed(name);
                        }}
                        validationError={feedback.error(
                            name,
                            (name === 'userName' || name === 'password' ? !value : errors.length > 0) && validationMessages[name](value),
                            // handleLogin explains a missing user name or password itself
                            name !== 'userName' && name !== 'password' && hasTriggeredValidations,
                        )}
                        className={css.input}
                        type={name === 'userName' ? 'text' : 'password'}
                        autoComplete={autoComplete}
                    />
                )}
            />
        );
    }

    useEffect(() => {
        if (initialValues.userName && initialValues.password) {
            loginButton.current?.click();
        }

        // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
    }, []);

    return (
        <Form
            data-testid="loginForm"
            className={css.form}
            defaultValue={initialValues}
            validations={changePassword ? changePasswordValidations : undefined}
            reportValidity={false}
            onKeyDown={submitOnEnter}
            onSubmit={(_event, { draft }) => handleLogin(draft)}
        >
            {success && <div className={css.changePasswordMessage}>{success}</div>}

            <div className={css.title}>{t('profile.login.title')}</div>
            {!changePassword && (
                <>
                    <div className={css.label}>{t('profile.login.username')}</div>
                    {input('userName', 'username')}
                </>
            )}
            {changePassword && (
                <Field
                    name="userName"
                    render={({ value }) => <input value={value} type="text" autoComplete="username" style={{ display: 'none' }} readOnly />}
                />
            )}

            <div className={css.label}>{changePassword ? t('profile.login.oldPassword') : t('profile.login.password')}</div>
            {input('password', 'current-password')}

            {changePassword && (
                <>
                    <div className={css.label}>{t('profile.login.newPassword')}</div>
                    {input('newPassword', 'new-password')}

                    <div className={css.label}>{t('profile.login.repeatNewPassword')}</div>
                    {input('newPasswordRepeat', 'new-password')}
                </>
            )}

            <NavLink to="/forgotPassword" className={css.forgotPassword}>
                {t('profile.forgotPassword.link')}
            </NavLink>

            <div className={css.captcha}>
                {config?.turnstileCaptcha.siteKey && isCaptchaEnabled && (
                    <Turnstile
                        ref={turnstileRef}
                        siteKey={
                            typeof testEnvironmentSecretToken === 'string'
                                ? TURNSTILE_TEST_ENVIRONMENT_SITE_KEY
                                : config.turnstileCaptcha.siteKey
                        }
                        options={{ theme: 'light', size: 'flexible', action: 'login' }}
                        onSuccess={setTurnstileToken}
                    />
                )}
                <div className={inputCss.validationErrorLabel}>{feedback.error('captcha', undefined)}</div>
            </div>

            <FormState selector={({ saveInProgress }) => saveInProgress}>
                {(processing) => (
                    <Button
                        ref={loginButton}
                        data-testid="loginButton"
                        className={css.button}
                        primary
                        inactive={processing || isTurnstileProcessing}
                        spinner={processing}
                        onClick={submitClosestForm}
                    >
                        {t('profile.login.login')}
                    </Button>
                )}
            </FormState>
        </Form>
    );
}
